import type Store from "@ember-data/store";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type {
  DmpDocumentContent,
  FindDocumentsCriteria,
  WebPSURLs
} from "parog-web/cnam/types";
import {
  DmpDocumentsSearchParams,
  DocumentStatus,
  DocumentVisibility
} from "parog-web/cnam/types";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";

import DocumentsRetrieveAccessFormSearchComponent from "parog-web/components/documents-retrieve-access-form/search";
import DocumentsRetrieveAccessFormShowComponent from "parog-web/components/documents-retrieve-access-form/show";
import type MedicModel from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";
import { State } from "./abstract/cnam-context";
import DmpContext from "./abstract/dmp-context";
import type { AuthenticationContext } from "./authentication-context";

export default class DmpConsultationContext extends DmpContext {
  @tracked patient: Patient;
  @tracked medic: MedicModel;
  @tracked declare searchParams: DmpDocumentsSearchParams;
  @tracked declare documents: DmpDocumentMetadata[];
  @tracked declare uniqueId?: string;
  @tracked declare currentDocumentMetadata?: DmpDocumentMetadata;
  @tracked declare currentDocumentContent?: DmpDocumentContent;
  @tracked declare webPSUrls?: WebPSURLs;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    medic: MedicModel,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    store: Store,
    requestManager
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      dmpAdapter,
      insiAdapter,
      store,
      requestManager
    );
    this.patient = patient;
    this.medic = medic;
    this.authenticationContext = authenticationContext;
    this.dmpAdapter = dmpAdapter;
    this.reset();
    this.states = [
      ...this.states,
      new State(
        "load-links",
        (): Promise<boolean> => {
          return Promise.resolve(!this.webPSUrls);
        },
        async (): Promise<void> => {
          this.webPSUrls = await this.dmpAdapter.td09GetWebPSURLs(
            this.patient.ins!,
            this.secretConnection!
          );
        }
      ),
      new State(
        "find-documents",
        (): Promise<boolean> => {
          return Promise.resolve(!this.currentDocumentMetadata);
        },
        async (): Promise<void> => {
          this.documents = await this.findDocuments(
            this.patient,
            this.store,
            this.paramsToFilterCriteria(this.searchParams)
          );
        },
        DocumentsRetrieveAccessFormSearchComponent
      ),
      new State(
        "show-document",
        (): Promise<boolean> => {
          return Promise.resolve(Boolean(this.currentDocumentMetadata));
        },
        async (): Promise<void> => {
          this.currentDocumentContent =
            await this.dmpAdapter.td32RetrieveContent(
              this.patient.ins!,
              this.currentDocumentMetadata!,
              this.secretConnection!
            );
        },
        DocumentsRetrieveAccessFormShowComponent
      )
    ];
  }

  @action
  reset(): void {
    super.reset();
    this.searchParams = new DmpDocumentsSearchParams(
      [],
      undefined,
      undefined,
      undefined,
      false,
      false,
      false,
      false,
      false
    );

    this.uniqueId = undefined;
    this.currentDocumentMetadata = undefined;
    this.currentDocumentContent = undefined;
    this.webPSUrls = undefined;
    this.successKey = undefined;
  }

  private paramsToFilterCriteria(
    params: DmpDocumentsSearchParams
  ): FindDocumentsCriteria {
    const cnamApiParams: FindDocumentsCriteria = {
      typeCodes: params.typeCodes
    };

    const startDate = params.serviceStartTimeFrom
      ? new Date(params.serviceStartTimeFrom)
      : undefined;
    const endDate = params.serviceStopTimeTo
      ? new Date(params.serviceStopTimeTo)
      : undefined;
    endDate?.setHours(23, 59, 59);
    const submissionDate = params.submissionStartTimeFrom
      ? new Date(params.submissionStartTimeFrom)
      : undefined;

    cnamApiParams.serviceStartTimeFrom = startDate;
    cnamApiParams.serviceStopTimeTo = endDate;

    /// Submission Date

    cnamApiParams.submissionStartTimeFrom = submissionDate;

    /// Filters

    const statuses: Array<DocumentStatus> = [];
    if (params.archived) statuses.push(DocumentStatus.ARCHIVED);
    if (params.obsolete) statuses.push(DocumentStatus.DEPRECATED);

    if (statuses.length > 0) {
      cnamApiParams.statuses = statuses;
    }

    const confidentialities: Array<DocumentVisibility> = [];
    if (params.hiddenToPatient)
      confidentialities.push(DocumentVisibility.HIDDEN_TO_PATIENT);
    if (params.hiddenToHealthcareProfessional)
      confidentialities.push(
        DocumentVisibility.HIDDEN_TO_HEALTHCARE_PROFESSIONAL
      );
    if (params.hiddenToLegalRepresentatives)
      confidentialities.push(
        DocumentVisibility.HIDDEN_TO_LEGAL_REPRESENTATIVES
      );

    if (confidentialities.length > 0) {
      cnamApiParams.visibilities = confidentialities;
    }

    return cnamApiParams;
  }
}
